import { useEffect, useState, useCallback } from "react";
import { useAuth, useClerk } from "@clerk/react";
import {
  api,
  setUnauthorizedHandler,
  setTokenGetter,
} from "./api.js";
import SignIn from "./screens/SignIn.jsx";
import Today from "./screens/Today.jsx";
import Submit from "./screens/Submit.jsx";
import SupervisorReview from "./screens/SupervisorReview.jsx";
import Admin from "./screens/Admin.jsx";

export default function App() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  // For workers: today | submit
  // For supervisors/admins: review | today (they can still log their own trips
  // if they're filling in for a worker role too, but default view is the queue)
  const [screen, setScreen] = useState("today");
  const [error, setError] = useState(null);
  // The (year,month) the worker was viewing when they tapped "Submit month".
  // We thread this through to Submit.jsx so it submits the *viewed* period,
  // not today's calendar month — otherwise a worker on May 2 trying to
  // resubmit April's returned report would submit May instead.
  const [submitPeriod, setSubmitPeriod] = useState(null);

  const refresh = useCallback(async () => {
    if (!isLoaded) return; // Clerk SDK not ready yet
    setLoading(true);
    setError(null);
    try {
      if (!isSignedIn) {
        setMe(null);
      } else {
        const fetched = await api.me();
        setMe(fetched);
        setScreen((prev) => {
          if (prev !== "today") return prev;
          return fetched.role === "supervisor" || fetched.role === "admin"
            ? "review"
            : "today";
        });
      }
    } catch (e) {
      setError(e.message);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn]);

  // Register/unregister the Clerk token getter whenever sign-in state changes.
  // api.js reads this on every authenticated fetch instead of localStorage.
  useEffect(() => {
    if (isSignedIn) {
      setTokenGetter(() => getToken());
    } else {
      setTokenGetter(null);
    }
  }, [isSignedIn, getToken]);

  // IMPORTANT: this effect must remain declared AFTER the setTokenGetter effect
  // above. React runs useEffects in declaration order, so the token getter must
  // be registered before the first api.me() call fires.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any 401 from any API call (admin deactivated a worker mid-session,
  // backend invalidated a stored email, etc.) should drop the user back
  // on SignIn instead of leaving a persistent red "Unauthorized" banner
  // wherever they happened to be. api.js clears the stored email before
  // firing this; we just reset React state and surface a helpful message.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setMe(null);
      setScreen("today");
      setError("Your session is no longer valid. Please sign in again.");
      setLoading(false);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Pending-review count for the "To review" tab badge. Saves supervisors
  // a click per day — today the only way to answer "do I have work?" is
  // to open the tab. Refresh when the user is logged in, whenever they
  // leave the review tab (they just acted on items), and on window focus.
  const [pendingCount, setPendingCount] = useState(null);
  const isReviewer = me && (me.role === "supervisor" || me.role === "admin");

  const refreshPendingCount = useCallback(async () => {
    if (!isReviewer) return;
    try {
      const d = await api.listPending({ history: false });
      setPendingCount(d.submissions?.length ?? 0);
    } catch {
      // Non-fatal — keep stale count rather than clobbering the UI.
    }
  }, [isReviewer]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount, screen]);

  useEffect(() => {
    if (!isReviewer) return;
    const onFocus = () => refreshPendingCount();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isReviewer, refreshPendingCount]);

  if (!isLoaded || loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }

  if (!isSignedIn || !me) {
    return <SignIn error={error} />;
  }

  // isReviewer is declared above (badge code needs it before the early-return)
  const isAdmin = me.role === "admin";

  return (
    <div className="mx-auto flex h-full max-w-xl flex-col safe-top safe-bottom">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Endeavor Health Services
          </div>
          <div className="text-lg font-semibold text-slate-900">Mileage</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-slate-800">
            {me.fullName}
          </div>
          <div className="text-xs capitalize text-slate-500">{me.role}</div>
          <button
            className="text-xs text-slate-500 underline"
            onClick={() => {
              signOut();
              setMe(null);
              setScreen("today");
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Supervisors/admins get a tab switcher. Workers have no tabs. */}
      {isReviewer && (
        <nav className="flex border-b border-slate-200 bg-white text-sm">
          <TabButton
            label="To review"
            active={screen === "review"}
            onClick={() => setScreen("review")}
            badge={
              // Only show when count > 0 and we're not already on the tab.
              // Supervisors already see the list while they're on the tab;
              // the badge would be redundant and distract from 'done' state.
              pendingCount && pendingCount > 0 && screen !== "review"
                ? pendingCount
                : null
            }
          />
          <TabButton
            label="My mileage"
            active={screen === "today" || screen === "submit"}
            onClick={() => setScreen("today")}
          />
          {isAdmin && (
            <TabButton
              label="Admin"
              active={screen === "admin"}
              onClick={() => setScreen("admin")}
            />
          )}
        </nav>
      )}

      <main className="flex-1 overflow-y-auto">
        {screen === "review" && isReviewer && <SupervisorReview me={me} />}
        {screen === "admin" && isAdmin && <Admin />}
        {screen === "today" && (
          <Today
            me={me}
            onGoSubmit={(period) => {
              setSubmitPeriod(period || null);
              setScreen("submit");
            }}
          />
        )}
        {screen === "submit" && (
          <Submit
            me={me}
            period={submitPeriod || me.currentPeriod}
            onBack={() => setScreen("today")}
            onSubmitted={async () => {
              await refresh();
              setScreen("today");
            }}
          />
        )}
      </main>
    </div>
  );
}

function TabButton({ label, active, onClick, badge = null }) {
  return (
    <button
      onClick={onClick}
      className={
        "flex-1 py-3 text-center font-medium transition " +
        (active
          ? "border-b-2 border-endeavor-600 text-endeavor-700"
          : "border-b-2 border-transparent text-slate-500 active:bg-slate-50")
      }
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null && (
          <span
            // Small pill to match the tab's existing visual weight. Using
            // endeavor-600 on inactive tabs too so the count reads as a
            // "needs attention" cue regardless of which tab is active.
            className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-endeavor-600 px-1.5 text-[10px] font-bold text-white tabular-nums"
            aria-label={`${badge} pending`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
    </button>
  );
}
